import type { BootstrapResult, LevelAssessmentModel } from "@shared/labs/level-assessment-model";
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { rpcClient } from "@/lib/rpc";
import { BootstrapProgress, isBootstrapFullSuccess, scopesFromModel } from "./bootstrap-progress";

/**
 * `/labs/level-assessment/models` — list every evaluation model on disk,
 * with actions per row:
 *   - **Re-bootstrap**: rerun the LLM bootstrap from the current rubric.
 *     Surfaced as a primary action when the rubric is stale (edited after
 *     the last bootstrap).
 *   - **Excluir**: remove the model directory. Disabled on the seed.
 *
 * Editing the rubric itself happens in the config screen (PR 5 will add
 * an inline edit UI here too). The listing is read-only metadata + the
 * two actions above + the "+ Novo modelo" link.
 */

/** True when the rubric was edited after the last successful bootstrap.
 *  We compare ISO timestamps as strings (lexicographic == chronological
 *  for ISO-8601). `lastBootstrapAt: null` means "never bootstrapped" → stale. */
function isRubricStale(model: LevelAssessmentModel): boolean {
  if (model.lastBootstrapAt === null) return true;
  return model.rubricUpdatedAt > model.lastBootstrapAt;
}

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

interface BootstrapState {
  modelId: string;
  running: boolean;
  result: BootstrapResult | null;
}

/** Slug a free-text string into a valid model id (lowercase, hyphenated). */
function slugForModelId(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function ModelsListingPage() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [models, setModels] = useState<LevelAssessmentModel[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bootstrap, setBootstrap] = useState<BootstrapState | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  // Which model's calibration scope picker is open (models with disciplines
  // must calibrate per-discipline; cross-only models start directly).
  const [calibPickerId, setCalibPickerId] = useState<string | null>(null);
  const [startingCalib, setStartingCalib] = useState(false);
  // Inline "duplicate" form: which model is being copied + the new id/name.
  const [dupForm, setDupForm] = useState<{
    sourceId: string;
    newId: string;
    newName: string;
  } | null>(null);
  const [duplicating, setDuplicating] = useState(false);
  // Which model's matrix JSON is being fetched for download.
  const [downloading, setDownloading] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const res = (await rpcClient.labs.levelAssessmentModelsList({})) as {
        models: LevelAssessmentModel[];
      };
      setModels(res.models);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erro inesperado");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = (await rpcClient.labs.levelAssessmentModelsList({})) as {
          models: LevelAssessmentModel[];
        };
        if (cancelled) return;
        setModels(res.models);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Erro inesperado");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function runBootstrap(model: LevelAssessmentModel) {
    if (bootstrap?.running) {
      toast({
        title: "Bootstrap em andamento",
        description: "Aguarde o bootstrap atual terminar antes de iniciar outro.",
        variant: "destructive",
      });
      return;
    }
    setBootstrap({ modelId: model.id, running: true, result: null });
    try {
      const result = (await rpcClient.labs.levelAssessmentModelBootstrap({
        modelId: model.id,
      })) as BootstrapResult;
      setBootstrap({ modelId: model.id, running: false, result });
      const fullSuccess = isBootstrapFullSuccess(result, scopesFromModel(model));
      toast({
        title: fullSuccess ? "Bootstrap completo" : "Bootstrap parcial",
        description: fullSuccess
          ? `${model.name} re-bootstrapado com sucesso.`
          : "Alguns steps falharam — veja o painel para detalhes e tente novamente.",
        variant: fullSuccess ? "default" : "destructive",
      });
      await reload();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro inesperado";
      setBootstrap({ modelId: model.id, running: false, result: null });
      toast({ title: "Falha no bootstrap", description: message, variant: "destructive" });
    }
  }

  /** Create a calibration session for the model (optionally discipline-scoped)
   *  and jump into the guided workflow. A model WITH disciplines can't be
   *  evaluated cross-only, so the caller scopes those to a discipline. */
  async function startCalibration(model: LevelAssessmentModel, disciplineId: string | null) {
    if (startingCalib) return;
    setStartingCalib(true);
    try {
      const res = (await rpcClient.labs.levelAssessmentCalibrationSessionCreate({
        modelId: model.id,
        disciplineId,
        kind: disciplineId ? "new_discipline" : "ongoing",
      })) as { session: { id: string } };
      navigate(`/labs/level-assessment/calibrate/${res.session.id}`);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro inesperado";
      toast({ title: "Falha ao iniciar calibração", description: message, variant: "destructive" });
    } finally {
      setStartingCalib(false);
    }
  }

  function onCalibrateClick(model: LevelAssessmentModel) {
    if (model.disciplines.length === 0) {
      void startCalibration(model, null);
    } else {
      setCalibPickerId((id) => (id === model.id ? null : model.id));
    }
  }

  function openDuplicate(model: LevelAssessmentModel) {
    setDupForm({
      sourceId: model.id,
      newId: slugForModelId(`${model.id}-copia`),
      newName: `${model.name} (cópia)`,
    });
  }

  async function submitDuplicate() {
    if (!dupForm || duplicating) return;
    setDuplicating(true);
    try {
      await rpcClient.labs.levelAssessmentModelDuplicate({
        sourceModelId: dupForm.sourceId,
        newId: dupForm.newId,
        newName: dupForm.newName.trim(),
      });
      toast({
        title: "Modelo duplicado",
        description: `"${dupForm.newName.trim()}" criado a partir de ${dupForm.sourceId}.`,
      });
      setDupForm(null);
      await reload();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro inesperado";
      toast({ title: "Falha ao duplicar", description: message, variant: "destructive" });
    } finally {
      setDuplicating(false);
    }
  }

  /** Download the model's full competency matrix (cross + disciplinas) as a
   *  JSON file in the shared import/export contract — the file re-imports
   *  through the matrix upload in the create/edit wizard. */
  async function downloadMatrix(model: LevelAssessmentModel) {
    if (downloading) return;
    setDownloading(model.id);
    try {
      const doc = await rpcClient.labs.levelAssessmentModelMatrixExport({ modelId: model.id });
      const blob = new Blob([JSON.stringify(doc, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${model.id}-competency-matrix.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro inesperado";
      toast({ title: "Falha ao baixar a matriz", description: message, variant: "destructive" });
    } finally {
      setDownloading(null);
    }
  }

  async function deleteModel(model: LevelAssessmentModel) {
    if (model.isSeed) return;
    const confirmed = window.confirm(
      `Excluir o modelo "${model.name}" e tudo dentro dele (calibração, prompts, signal matrix)? Essa ação não pode ser desfeita.`,
    );
    if (!confirmed) return;
    setDeleting(model.id);
    try {
      await rpcClient.labs.levelAssessmentModelDelete({ modelId: model.id });
      toast({ title: "Modelo excluído", description: `"${model.name}" removido.` });
      await reload();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro inesperado";
      toast({ title: "Falha ao excluir", description: message, variant: "destructive" });
    } finally {
      setDeleting(null);
    }
  }

  return (
    <div className="ci-stripe ci-stripe-page">
      <div className="ci-narrow fade-up" style={{ width: "100%", maxWidth: 1100 }}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <Link
            href="/labs/level-assessment"
            style={{
              fontSize: 12,
              color: "var(--r-ink-muted)",
              textDecoration: "none",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            ← Voltar para avaliação
          </Link>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 16,
              flexWrap: "wrap",
              marginTop: 8,
            }}
          >
            <div>
              <h1
                style={{
                  margin: "0 0 6px",
                  fontSize: 28,
                  fontWeight: 600,
                  color: "var(--r-ink)",
                  letterSpacing: "-0.01em",
                }}
              >
                Modelos de avaliação
              </h1>
              <p
                style={{
                  margin: 0,
                  fontSize: 14,
                  color: "var(--r-ink-muted)",
                  lineHeight: 1.55,
                  maxWidth: 760,
                }}
              >
                Cada modelo define seus próprios eixos, níveis e rubrica. O bootstrap gera a matriz
                de sinais e os prompts via LLM (Opus, ~30-60s, ~5-7 chamadas).
              </p>
            </div>
            <Link
              href="/labs/level-assessment/models/new"
              className="btn primary"
              data-testid="models-new-link"
              style={{ textDecoration: "none" }}
            >
              + Novo modelo
            </Link>
          </div>
        </div>

        {error && (
          <div
            className="card"
            style={{ padding: "12px 16px", marginBottom: 16, color: "var(--r-coral, #f3354b)" }}
          >
            {error}
          </div>
        )}

        {/* Models list */}
        <div style={{ display: "grid", gap: 16 }}>
          {models === null && (
            <div style={{ color: "var(--r-ink-muted)", fontSize: 13 }}>Carregando modelos…</div>
          )}
          {models?.map((model) => {
            const stale = isRubricStale(model);
            const isThisBootstrapping = bootstrap?.modelId === model.id;
            return (
              <div
                key={model.id}
                className="card"
                style={{ padding: 20 }}
                data-testid={`model-row-${model.id}`}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 16,
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 4,
                      }}
                    >
                      <h2
                        style={{
                          margin: 0,
                          fontSize: 18,
                          fontWeight: 600,
                          color: "var(--r-ink)",
                          letterSpacing: "-0.01em",
                        }}
                      >
                        {model.name}
                      </h2>
                      {model.isSeed && (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: "var(--r-ink-muted)",
                            background: "var(--r-sand, #f4f1ea)",
                            padding: "2px 8px",
                            borderRadius: 999,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                          }}
                        >
                          seed
                        </span>
                      )}
                      {stale && !isThisBootstrapping && (
                        <span
                          data-testid="model-stale-badge"
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: "#fff",
                            background: "var(--r-amber, #b07811)",
                            padding: "2px 8px",
                            borderRadius: 999,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                          }}
                          title="A rubrica mudou desde o último bootstrap — re-bootstrap recomendado."
                        >
                          Rubrica stale
                        </span>
                      )}
                    </div>
                    <p
                      style={{
                        margin: "0 0 10px",
                        fontSize: 13,
                        color: "var(--r-ink-muted)",
                        lineHeight: 1.55,
                      }}
                    >
                      {model.description || (
                        <em style={{ color: "var(--r-ink-faint, #c7c2bb)" }}>(sem descrição)</em>
                      )}
                    </p>
                    <div
                      style={{
                        display: "flex",
                        gap: 16,
                        fontSize: 12,
                        color: "var(--r-ink-muted)",
                        flexWrap: "wrap",
                      }}
                    >
                      <span>
                        <strong style={{ color: "var(--r-ink)" }}>{model.crossAxes.length}</strong>{" "}
                        cross
                        {model.disciplines.length > 0 ? (
                          <>
                            {" · "}
                            <strong style={{ color: "var(--r-ink)" }}>
                              {model.disciplines.length}
                            </strong>{" "}
                            disc.
                          </>
                        ) : null}
                      </span>
                      <span>
                        <strong style={{ color: "var(--r-ink)" }}>
                          L{model.levels.min}–L{model.levels.max}
                        </strong>
                      </span>
                      <span>
                        rubrica:{" "}
                        <span style={{ fontVariantNumeric: "tabular-nums" }}>
                          {formatTimestamp(model.rubricUpdatedAt)}
                        </span>
                      </span>
                      <span>
                        bootstrap:{" "}
                        <span style={{ fontVariantNumeric: "tabular-nums" }}>
                          {model.lastBootstrapAt ? formatTimestamp(model.lastBootstrapAt) : "nunca"}
                        </span>
                      </span>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexShrink: 0,
                      flexWrap: "wrap",
                      justifyContent: "flex-end",
                    }}
                  >
                    <button
                      type="button"
                      className={stale ? "btn primary" : "btn ghost"}
                      onClick={() => runBootstrap(model)}
                      disabled={isThisBootstrapping}
                      data-testid={`model-bootstrap-${model.id}`}
                      title={
                        stale
                          ? "A rubrica mudou desde o último bootstrap"
                          : "Rerodar o bootstrap LLM (não afeta calibração)"
                      }
                    >
                      {isThisBootstrapping && bootstrap?.running
                        ? "Bootstrapando…"
                        : stale
                          ? "Re-bootstrap"
                          : "Bootstrap"}
                    </button>
                    <button
                      type="button"
                      className="btn primary"
                      onClick={() => onCalibrateClick(model)}
                      disabled={startingCalib}
                      data-testid={`model-calibrate-${model.id}`}
                      title="Calibrar o modelo com perfis de referência"
                    >
                      Calibrar
                    </button>
                    <Link
                      href={`/labs/level-assessment/models/${model.id}/edit`}
                      className="btn ghost"
                      data-testid={`model-edit-${model.id}`}
                      style={{ textDecoration: "none" }}
                      title="Editar identidade, competências cross e disciplinas"
                    >
                      Editar
                    </Link>
                    <button
                      type="button"
                      className="btn ghost"
                      onClick={() => downloadMatrix(model)}
                      disabled={downloading === model.id}
                      data-testid={`model-matrix-download-${model.id}`}
                      title="Baixar a matriz de competências (cross + disciplinas) como JSON"
                    >
                      {downloading === model.id ? "Baixando…" : "Baixar JSON"}
                    </button>
                    <button
                      type="button"
                      className="btn ghost"
                      onClick={() => openDuplicate(model)}
                      disabled={duplicating}
                      data-testid={`model-duplicate-${model.id}`}
                      title="Criar uma cópia editável deste modelo (eixos, disciplinas, rubricas, prompts)"
                    >
                      Duplicar
                    </button>
                    <button
                      type="button"
                      className="btn ghost"
                      onClick={() => deleteModel(model)}
                      disabled={model.isSeed || deleting === model.id}
                      data-testid={`model-delete-${model.id}`}
                      title={
                        model.isSeed
                          ? "O modelo seed não pode ser excluído"
                          : "Excluir esse modelo e tudo dentro dele"
                      }
                      style={{ color: model.isSeed ? undefined : "var(--r-coral, #f3354b)" }}
                    >
                      {deleting === model.id ? "Excluindo…" : "Excluir"}
                    </button>
                  </div>
                </div>

                {/* Duplicate form — inline, when this model is being copied. */}
                {dupForm?.sourceId === model.id && (
                  <div
                    data-testid={`model-duplicate-form-${model.id}`}
                    style={{
                      marginTop: 16,
                      paddingTop: 16,
                      borderTop: "1px solid var(--r-ink-faint, #e9e3d8)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--r-ink)",
                        marginBottom: 8,
                      }}
                    >
                      Duplicar "{model.name}"
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(180px, 240px) 1fr",
                        gap: 12,
                      }}
                    >
                      <div>
                        <div style={dupLabelStyle}>Novo id (slug)</div>
                        <input
                          value={dupForm.newId}
                          onChange={(e) =>
                            setDupForm((f) =>
                              f ? { ...f, newId: slugForModelId(e.target.value) } : f,
                            )
                          }
                          disabled={duplicating}
                          data-testid="duplicate-id-input"
                          style={{
                            ...dupInputStyle,
                            fontFamily: "ui-monospace, SFMono-Regular, monospace",
                          }}
                        />
                      </div>
                      <div>
                        <div style={dupLabelStyle}>Novo nome</div>
                        <input
                          value={dupForm.newName}
                          onChange={(e) =>
                            setDupForm((f) => (f ? { ...f, newName: e.target.value } : f))
                          }
                          disabled={duplicating}
                          data-testid="duplicate-name-input"
                          style={dupInputStyle}
                        />
                      </div>
                    </div>
                    <div
                      style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}
                    >
                      <button
                        type="button"
                        className="btn ghost"
                        onClick={() => setDupForm(null)}
                        disabled={duplicating}
                        data-testid="duplicate-cancel"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        className="btn primary"
                        onClick={submitDuplicate}
                        disabled={
                          duplicating || !dupForm.newId || dupForm.newName.trim().length === 0
                        }
                        data-testid="duplicate-submit"
                      >
                        {duplicating ? "Duplicando…" : "Duplicar modelo"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Calibration scope picker — models with disciplines calibrate
                    one discipline at a time (cross-only eval isn't valid). */}
                {calibPickerId === model.id && model.disciplines.length > 0 && (
                  <div
                    data-testid={`model-calibrate-picker-${model.id}`}
                    style={{
                      marginTop: 16,
                      paddingTop: 16,
                      borderTop: "1px solid var(--r-ink-faint, #e9e3d8)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--r-ink)",
                        marginBottom: 8,
                      }}
                    >
                      Qual disciplina você quer calibrar?
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {model.disciplines.map((d) => (
                        <button
                          key={d.id}
                          type="button"
                          className="btn ghost"
                          onClick={() => startCalibration(model, d.id)}
                          disabled={startingCalib}
                          data-testid={`model-calibrate-discipline-${model.id}-${d.id}`}
                        >
                          {d.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bootstrap panel — visible only when this model has an
                    active or recently-finished run. */}
                {isThisBootstrapping && (
                  <div
                    style={{
                      marginTop: 16,
                      paddingTop: 16,
                      borderTop: "1px solid var(--r-ink-faint, #e9e3d8)",
                    }}
                  >
                    <BootstrapProgress
                      running={bootstrap?.running ?? false}
                      result={bootstrap?.result ?? null}
                      scopes={scopesFromModel(model)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const dupLabelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: "var(--r-ink)",
  marginBottom: 4,
};

const dupInputStyle: React.CSSProperties = {
  width: "100%",
  height: 36,
  padding: "0 10px",
  border: "1px solid var(--r-ink-faint, #e9e3d8)",
  borderRadius: 6,
  background: "var(--r-bg, #fffaf3)",
  color: "var(--r-ink)",
  fontSize: 13,
};
